import { useDeferredValue, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import monstersCatalog from "../../catalogs/monstersCatalog.json";

import CampaignDialog from "./CampaignDialog";

const EncounterDialog = ({ form, isOpen, onChange, onClose, onSubmit }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [challengeRating, setChallengeRating] = useState("");
  const [visibleCount, setVisibleCount] = useState(60);
  const deferredSearch = useDeferredValue(search);
  const selectedMonsters = form.selectedMonsters ?? [];
  const challengeRatings = [...new Set(monstersCatalog.entries.map((monster) => monster.challengeRating).filter(Boolean))];
  const catalogMonsters = monstersCatalog.entries.filter((monster) => {
    const matchesSearch = `${monster.name.ru} ${monster.name.en}`
      .toLowerCase()
      .includes(deferredSearch.trim().toLowerCase());
    return matchesSearch && (!challengeRating || monster.challengeRating === challengeRating);
  });

  useEffect(() => {
    setVisibleCount(60);
  }, [deferredSearch, challengeRating]);

  const handleCatalogScroll = (event) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 48) {
      setVisibleCount((current) => Math.min(current + 60, catalogMonsters.length));
    }
  };

  const toggleMonster = (monster) => {
    onChange((current) => {
      const selected = current.selectedMonsters ?? [];
      const existing = selected.find((entry) => entry.catalogId === monster.id);
      if (existing) {
        return { ...current, selectedMonsters: selected.filter((entry) => entry.catalogId !== monster.id) };
      }
      const maxHp = Number.parseInt(monster.statBlock.hp, 10) || 0;
      return {
        ...current,
        selectedMonsters: [...selected, { catalogId: monster.id, name: monster.name.ru, quantity: 1, maxHp }],
      };
    });
  };

  const updateQuantity = (catalogId, quantity) => {
    onChange((current) => ({
      ...current,
      selectedMonsters: current.selectedMonsters.map((monster) =>
        monster.catalogId === catalogId ? { ...monster, quantity: Math.max(1, Number(quantity) || 1) } : monster
      ),
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!form.title.trim()) return;

    onSubmit({
      ...form,
      title: form.title.trim(),
      description: form.description.trim(),
      notes: form.notes.trim(),
      enemyText: "",
    });
  };

  return (
    <CampaignDialog
      description={t("campaigns.encounterDialogHint")}
      isOpen={isOpen}
      onClose={onClose}
      title={t("campaigns.addEncounter")}
      titleEyebrow={t("campaigns.sections.encountersEyebrow")}
    >
      <form className="campaign-form-grid" onSubmit={handleSubmit}>
        <label htmlFor="encounter-title">
          {t("campaigns.form.encounterTitle")}
          <input
            id="encounter-title"
            name="encounter-title"
            value={form.title}
            onChange={(event) =>
              onChange((current) => ({ ...current, title: event.target.value }))
            }
          />
        </label>
        <label htmlFor="encounter-description">
          {t("campaigns.form.description")}
          <textarea
            id="encounter-description"
            name="encounter-description"
            value={form.description}
            onChange={(event) =>
              onChange((current) => ({
                ...current,
                description: event.target.value,
              }))
            }
          />
        </label>
        <section className="encounter-monster-picker" aria-label="Выбор существ">
          <div className="encounter-picker-controls">
            <label htmlFor="encounter-monster-search">
              Поиск существа
              <input id="encounter-monster-search" type="search" value={search} onChange={(event) => setSearch(event.target.value)} />
            </label>
            <label htmlFor="encounter-challenge-rating">
              Класс опасности
              <select id="encounter-challenge-rating" value={challengeRating} onChange={(event) => setChallengeRating(event.target.value)}>
                <option value="">Все классы</option>
                {challengeRatings.map((rating) => <option key={rating} value={rating}>{rating}</option>)}
              </select>
            </label>
          </div>
          <div className="encounter-monster-catalog" onScroll={handleCatalogScroll}>
            {catalogMonsters.slice(0, visibleCount).map((monster) => {
              const checked = selectedMonsters.some((entry) => entry.catalogId === monster.id);
              return <label className="encounter-monster-option" key={monster.id}>
                <input type="checkbox" checked={checked} onChange={() => toggleMonster(monster)} />
                <span>{monster.name.ru}</span>
                <small>КО {monster.challengeRating || "-"} · HP {monster.statBlock.hp || "-"}</small>
              </label>;
            })}
            {visibleCount < catalogMonsters.length && <p className="encounter-catalog-loading">Прокрутите вниз, чтобы загрузить ещё</p>}
          </div>
          {selectedMonsters.length > 0 && <div className="encounter-selected-monsters">
            {selectedMonsters.map((monster) => <label key={monster.catalogId}>
              {monster.name}
              <input aria-label={`Количество: ${monster.name}`} min="1" type="number" value={monster.quantity} onChange={(event) => updateQuantity(monster.catalogId, event.target.value)} />
            </label>)}
          </div>}
        </section>
        <label htmlFor="encounter-notes">
          {t("common.notes")}
          <textarea
            id="encounter-notes"
            name="encounter-notes"
            value={form.notes}
            onChange={(event) =>
              onChange((current) => ({ ...current, notes: event.target.value }))
            }
          />
        </label>
        <label htmlFor="encounter-experience">
          Опыт за победу
          <input id="encounter-experience" min="0" type="number" value={form.experienceReward ?? 0} onChange={(event) => onChange((current) => ({ ...current, experienceReward: event.target.value }))} />
        </label>
        <label htmlFor="encounter-rewards">
          Награды и предметы
          <textarea id="encounter-rewards" placeholder="По одному предмету с новой строки" value={(form.rewardItems ?? []).join("\n")} onChange={(event) => onChange((current) => ({ ...current, rewardItems: event.target.value.split("\n").map((value) => value.trim()).filter(Boolean) }))} />
        </label>
        <footer className="campaign-dialog-footer">
          <button className="campaign-ghost-button" type="button" onClick={onClose}>
            {t("common.cancel")}
          </button>
          <button className="campaign-primary-button" type="submit">
            {t("common.create")}
          </button>
        </footer>
      </form>
    </CampaignDialog>
  );
};

export default EncounterDialog;
